import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Alert } from 'src/components/Alert';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { ClientSearchResult, User } from 'src/models/user/_types';
import { ClientAutocomplete } from 'src/shared/ClientAutocomplete';

interface FindAccountDialogProps {
  open: boolean;
  onClose?: () => void;
  onSelectUser: (user: User) => void;
}

export function FindAccountDialog({
  open,
  onClose,
  onSelectUser,
}: FindAccountDialogProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.findAccount',
  });
  const { session } = useOptionalSession();
  const location = useLocation();

  // Get search type from platform settings
  const searchType = session?.checkInSearch || 'name';

  // Determine if we're in kiosk mode based on the route path
  const isKiosk = location.pathname.startsWith('/kiosk/');

  const handleSelectClient = (
    client: ClientSearchResult | ClientSearchResult[] | null
  ) => {
    if (client && !Array.isArray(client)) {
      // Convert ClientSearchResult to User format
      const user = {
        id: client.clientId || client.id,
        clientId: client.clientId,
        firstName: client.name?.split(' ')[0] || '',
        lastName: client.name?.split(' ').slice(1).join(' ') || '',
        email: client.email || '',
        profileImageUrl: client.profileImage,
        formattedName: client.name || '',
        kioskPinRegistered: client.kioskPinRegistered,
      } as unknown as User;

      onSelectUser(user);
    }
  };

  const getSearchPlaceholder = () => {
    switch (searchType) {
      case 'email':
        return t('searchByEmail');
      case 'phone_number':
        return t('searchByPhone');
      case 'name':
      default:
        return t('searchByName');
    }
  };

  const secondary = (option: ClientSearchResult) => {
    if (searchType === 'phone_number') {
      return option.phoneNumber || '';
    }

    return option.email || '';
  };

  return (
    <Dialog
      open={open}
      title={t('title')}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <Stack spacing={1}>
        <Alert severity="info">
          {getSearchPlaceholder()} {t('searchLabel')}
        </Alert>
        <ClientAutocomplete
          value={null}
          onChange={handleSelectClient}
          label=""
          placeholder={getSearchPlaceholder()}
          autoFocus
          searchTypes={['user']}
          secondary={secondary}
          includeUserSearch={isKiosk}
          searchBy={searchType as 'email' | 'phone_number' | 'name'}
        />
      </Stack>
    </Dialog>
  );
}
